import { lazy, Suspense } from 'preact/compat'
import { Redirect, Route, Router, Switch } from 'wouter'
import { useAuth } from './hooks/useAuth'
import { AppLayout } from './layouts/AppLayout'
// Layouts
import { AuthLayout } from './layouts/AuthLayout'
import { GuardianLayout } from './layouts/GuardianLayout'
import { CallbackPage } from './pages/auth/CallbackPage'
// Pages
import { LandingPage } from './pages/auth/LandingPage'
import { SignupPage } from './pages/auth/SignupPage'
import { LinkPendingPage } from './pages/child/LinkPendingPage'
import { LinkRequestPage } from './pages/child/LinkRequestPage'
import { ChildDetailPage } from './pages/guardian/ChildDetailPage'
import { DashboardPage } from './pages/guardian/DashboardPage'
import { RequestsPage } from './pages/guardian/RequestsPage'
import { CompletePage } from './pages/guardian-link/CompletePage'
import { ResolvePage } from './pages/guardian-link/ResolvePage'
import { ProfilePage } from './pages/profile/ProfilePage'
import { SettingsPage } from './pages/profile/SettingsPage'
import { ROUTES } from './routes'
import { DEV_SKIP_AUTH } from './store/auth'

// Lazy-loaded game page (only loads when user navigates to /play)
const GamePage = lazy(() => import('./pages/game/GamePage').then((m) => ({ default: m.GamePage })))

import type { ComponentChildren } from 'preact'
// Components
import { Loading } from './components/Loading'

export function App() {
  const { isInitialized, isLoading: _isLoading } = useAuth()

  // Show loading while Keycloak initializes
  if (!isInitialized.value) {
    return <Loading fullScreen message="Loading..." />
  }

  return (
    <Router>
      <Switch>
        {/* Public routes */}
        <Route path={ROUTES.HOME}>
          {DEV_SKIP_AUTH ? <Redirect to={ROUTES.PLAY} /> : <LandingPage />}
        </Route>
        <Route path={ROUTES.CALLBACK} component={CallbackPage} />
        <Route path={ROUTES.GUARDIAN_LINK_RESOLVE} component={ResolvePage} />

        {/* Protected routes */}
        <Route path={ROUTES.SIGNUP}>
          <ProtectedRoute requiresAuth requiresNoProfile>
            <AuthLayout>
              <SignupPage />
            </AuthLayout>
          </ProtectedRoute>
        </Route>

        <Route path={ROUTES.PROFILE}>
          <ProtectedRoute requiresAuth requiresProfile>
            <AppLayout>
              <ProfilePage />
            </AppLayout>
          </ProtectedRoute>
        </Route>

        <Route path={ROUTES.SETTINGS}>
          <ProtectedRoute requiresAuth requiresProfile>
            <AppLayout>
              <SettingsPage />
            </AppLayout>
          </ProtectedRoute>
        </Route>

        {/* Game route - lazy loaded */}
        <Route path={ROUTES.PLAY}>
          <ProtectedRoute requiresAuth requiresProfile>
            <Suspense fallback={<Loading fullScreen message="Loading game..." />}>
              <GamePage />
            </Suspense>
          </ProtectedRoute>
        </Route>

        {/* Child routes */}
        <Route path={ROUTES.CHILD_LINK_REQUEST}>
          <ProtectedRoute requiresAuth requiresProfile profileType="CHILD">
            <AppLayout>
              <LinkRequestPage />
            </AppLayout>
          </ProtectedRoute>
        </Route>

        <Route path={ROUTES.CHILD_LINK_PENDING}>
          <ProtectedRoute requiresAuth requiresProfile profileType="CHILD">
            <AppLayout>
              <LinkPendingPage />
            </AppLayout>
          </ProtectedRoute>
        </Route>

        {/* Guardian routes */}
        <Route path={ROUTES.GUARDIAN_LINK_COMPLETE}>
          <ProtectedRoute requiresAuth requiresProfile profileType="ADULT">
            <AppLayout>
              <CompletePage />
            </AppLayout>
          </ProtectedRoute>
        </Route>

        <Route path={ROUTES.GUARDIAN_DASHBOARD}>
          <ProtectedRoute requiresAuth requiresProfile profileType="ADULT">
            <GuardianLayout>
              <DashboardPage />
            </GuardianLayout>
          </ProtectedRoute>
        </Route>

        <Route path={ROUTES.GUARDIAN_CHILD}>
          <ProtectedRoute requiresAuth requiresProfile profileType="ADULT">
            <GuardianLayout>
              <ChildDetailPage />
            </GuardianLayout>
          </ProtectedRoute>
        </Route>

        <Route path={ROUTES.GUARDIAN_REQUESTS}>
          <ProtectedRoute requiresAuth requiresProfile profileType="ADULT">
            <GuardianLayout>
              <RequestsPage />
            </GuardianLayout>
          </ProtectedRoute>
        </Route>

        {/* Fallback */}
        <Route>
          <Redirect to={ROUTES.HOME} />
        </Route>
      </Switch>
    </Router>
  )
}

interface ProtectedRouteProps {
  children: ComponentChildren
  requiresAuth?: boolean
  requiresProfile?: boolean
  requiresNoProfile?: boolean
  profileType?: 'CHILD' | 'ADULT'
}

function ProtectedRoute({
  children,
  requiresAuth,
  requiresProfile,
  requiresNoProfile,
  profileType,
}: ProtectedRouteProps) {
  const { isAuthenticated, profile, isLoading } = useAuth()

  if (isLoading.value) {
    return <Loading fullScreen />
  }

  let redirectTo: string | null = null

  if (requiresAuth && !isAuthenticated.value) {
    redirectTo = ROUTES.HOME
  } else if (requiresProfile && !profile.value) {
    redirectTo = ROUTES.SIGNUP
  } else if (requiresNoProfile && profile.value) {
    redirectTo = ROUTES.PLAY
  } else if (profileType && profile.value?.type !== profileType) {
    redirectTo = ROUTES.PLAY
  }

  if (redirectTo) {
    return <Redirect to={redirectTo} />
  }

  return <>{children}</>
}
